// User-initiated, previewed sharing for an ordinary authenticated plan route.
// The message is data-minimal by construction: first chosen-name word + support
// window + opaque navigation. It never contains a private event/need title.

import { authenticatedNeedShareLink, discreetFirstName } from "@herefor/sharing";
import { Button, Dialog, StatusMessage } from "@herefor/design-system";
import {
  SHARE_SNIPPET_MESSAGES,
  SUPPORTED_PERSON_PLACEHOLDER_SHARE_MESSAGES,
} from "@herefor/product-language";
import { useMemo, useState } from "react";

export interface ShareSnippetProps {
  readonly publicPlanRef: string;
  readonly supportNeedId: string;
  readonly supportedPersonName: string;
  readonly supportedPersonIsPlaceholder: boolean;
  readonly dateLabel: string;
  readonly windowLabel: string;
}

type Feedback =
  "shared" | "messageCopied" | "linkCopied" | "copyUnavailable" | "shareUnavailable" | null;

export function ShareSnippet({
  publicPlanRef,
  supportNeedId,
  supportedPersonName,
  supportedPersonIsPlaceholder,
  dateLabel,
  windowLabel,
}: ShareSnippetProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [feedback, setFeedback] = useState<Feedback>(null);
  const link = useMemo(
    () =>
      authenticatedNeedShareLink({
        origin: window.location.origin,
        publicPlanRef,
        supportNeedId,
      }),
    [publicPlanRef, supportNeedId],
  );
  const personLabel = supportedPersonIsPlaceholder
    ? SUPPORTED_PERSON_PLACEHOLDER_SHARE_MESSAGES.neutralSubject
    : discreetFirstName(supportedPersonName);

  if (!link.ok || personLabel === null) return null;

  const defaultMessage = SHARE_SNIPPET_MESSAGES.defaultMessage(
    personLabel,
    dateLabel,
    windowLabel,
    link.value,
  );

  const begin = () => {
    setDraft(defaultMessage);
    setFeedback(null);
    setOpen(true);
  };
  const copy = async (
    value: string,
    success: Extract<Feedback, "messageCopied" | "linkCopied">,
  ) => {
    setFeedback((await copyText(value)) ? success : "copyUnavailable");
  };
  const share = async () => {
    try {
      await navigator.share({ text: draft });
      setFeedback("shared");
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setFeedback("shareUnavailable");
      }
    }
  };

  return (
    <div className="share-snippet" data-share-snippet={supportNeedId}>
      <Button onClick={begin} variant="secondary">
        {SHARE_SNIPPET_MESSAGES.action}
      </Button>

      <Dialog
        footer={
          <Button
            onClick={() => {
              setOpen(false);
            }}
            variant="secondary"
          >
            {SHARE_SNIPPET_MESSAGES.close}
          </Button>
        }
        onClose={() => {
          setOpen(false);
        }}
        open={open}
        title={SHARE_SNIPPET_MESSAGES.dialogTitle}
      >
        <p>{SHARE_SNIPPET_MESSAGES.preview}</p>
        <label className="share-snippet__field">
          <span>{SHARE_SNIPPET_MESSAGES.messageLabel}</span>
          <textarea
            data-share-preview="message"
            onChange={(event) => {
              setDraft(event.currentTarget.value);
              setFeedback(null);
            }}
            rows={6}
            value={draft}
          />
        </label>
        <div className="share-snippet__actions">
          {typeof navigator.share === "function" ? (
            <Button
              data-share-action="native"
              onClick={() => {
                void share();
              }}
              variant="primary"
            >
              {SHARE_SNIPPET_MESSAGES.nativeShare}
            </Button>
          ) : null}
          <Button
            data-share-action="copy-message"
            onClick={() => {
              void copy(draft, "messageCopied");
            }}
          >
            {SHARE_SNIPPET_MESSAGES.copyMessage}
          </Button>
          <Button
            data-share-action="copy-link"
            onClick={() => {
              void copy(link.value, "linkCopied");
            }}
          >
            {SHARE_SNIPPET_MESSAGES.copyLink}
          </Button>
        </div>
        {feedback === null ? null : (
          <StatusMessage tone={feedback.endsWith("Unavailable") ? "warning" : "success"}>
            {SHARE_SNIPPET_MESSAGES[feedback]}
          </StatusMessage>
        )}
      </Dialog>
    </div>
  );
}

async function copyText(value: string): Promise<boolean> {
  const clipboard = Reflect.get(navigator, "clipboard") as unknown;
  const writeText =
    typeof clipboard === "object" && clipboard !== null
      ? (Reflect.get(clipboard, "writeText") as ((text: string) => Promise<void>) | undefined)
      : undefined;
  try {
    if (typeof writeText === "function") {
      await writeText.call(clipboard, value);
      return true;
    }
  } catch {
    // A denied Clipboard API is exactly why the selection-based fallback exists.
  }

  const field = document.createElement("textarea");
  field.value = value;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.append(field);
  field.select();
  // The deprecated command is retained only as the old-browser fallback after the
  // modern Clipboard API is absent/denied; it preserves the bead's desktop button.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const copied = document.execCommand("copy");
  field.remove();
  return copied;
}

export const shareSnippetStylesheet = String.raw`
.share-snippet { margin-block-start: var(--herefor-space-3); }
.share-snippet__field { display: grid; gap: var(--herefor-space-2); font-weight: var(--herefor-font-weight-medium); }
.share-snippet__field textarea {
  box-sizing: border-box;
  inline-size: 100%;
  min-block-size: 8rem;
  padding: var(--herefor-space-3);
  border: 1px solid var(--herefor-color-border-strong);
  border-radius: var(--herefor-radius-md);
  color: var(--herefor-color-text);
  background: var(--herefor-color-surface);
  font: inherit;
  resize: vertical;
}
.share-snippet__field textarea:focus-visible { outline: var(--herefor-focus-ring); }
.share-snippet__actions { display: flex; flex-wrap: wrap; gap: var(--herefor-space-2); margin-block: var(--herefor-space-3); }
`;
